import './SentToSwitch.scss';

import { useContext, useEffect, useState } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';

import { CreateTabContext } from '@/components/App/NewOrder/ExistedOrder';
import { Button, Modal } from '@/components/Shared/ui';
import Select from '@/components/Shared/ui/select/select';
import { DictTypes } from '@/constants';
import { isDictHasAll, safeKey } from '@/services/helpers/dict';
import { useAppSelector } from '@/store';

import {
  type CreateAutoCreditProps,
  type ProductInfoInterface
} from '../../../../types/form-types';

type TSentTo = NonNullable<ProductInfoInterface['sentTo']>;

type SentToOption = {
  label: string;
  value: TSentTo;
};

const sentToLabels: Record<TSentTo, string> = {
  banks_leasing: 'Банки, Лизинг',
  banks: 'Банки',
  leasing: 'Лизинг'
};

const sentToOptions: SentToOption[] = (
  Object.keys(sentToLabels) as TSentTo[]
).map((value) => ({
  label: sentToLabels[value],
  value
}));

const SentToSwitch: React.FC = () => {
  const { isFieldsFrozen } = useContext(CreateTabContext);

  const { dicts } = useAppSelector((state) => state.dict);
  const { orderApp } = useAppSelector((state) => state.newOrder);
  const { dealer: userDealer } = useAppSelector((state) => state.userInfo);

  const { control, setValue } = useFormContext<CreateAutoCreditProps>();
  const sentTo = useWatch({ control, name: 'sentTo' });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedSentTo, setSelectedSentTo] = useState(sentTo);
  const [isLeasingEnabled, setIsLeasingEnabled] = useState(true);

  const currentDealer =
    dicts?.Dealers?.getBy.code[
      safeKey(orderApp?.productInfo.product.dealer ?? userDealer?.code)
    ];

  const handleOpenModal = () => {
    setIsModalOpen(true);
  };

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleSubmit = () => {
    if (!selectedSentTo || !isLeasingEnabled) return;
    const foundSentTo = sentToOptions.find(
      (option) => option.value === selectedSentTo
    );
    if (!foundSentTo) return;
    setValue('sentTo', foundSentTo.value);
    handleClose();

    if (foundSentTo.value === 'leasing' && currentDealer) {
      setValue('dealer', String(currentDealer.code));
      setValue('markModelYear', {
        brand: undefined,
        dealer: undefined,
        model: undefined,
        equipment: undefined,
        year: ''
      });
    }
  };

  useEffect(() => {
    if (selectedSentTo !== sentTo) {
      setSelectedSentTo(sentTo);
    }
  }, [sentTo]);

  useEffect(() => {
    if (
      !currentDealer ||
      !isDictHasAll(dicts, [DictTypes.LeasingIndividualsSettings])
    )
      return;

    if (currentDealer._dealerUuids.length > 0) {
      setIsLeasingEnabled(true);
      return;
    }

    const isDealerHasLeasing = dicts[
      DictTypes.LeasingIndividualsSettings
    ].content.some(
      (item) => item.dealer_uuid === currentDealer?.uuid && item.is_active
    );

    setIsLeasingEnabled(isDealerHasLeasing);
  }, [currentDealer, dicts]);

  return (
    <div className="sent-to-switch">
      <div className="sent-to-switch__text-container">
        <span className="sent-to-switch__label">Отправлять в</span>
        <strong className="sent-to-switch__value">
          {sentTo ? sentToLabels[sentTo] : ''}
        </strong>
      </div>
      {!isFieldsFrozen && isLeasingEnabled && (
        <>
          <Button
            variant="outlined"
            size="small"
            type="button"
            onClick={handleOpenModal}
          >
            Изменить
          </Button>
          <Modal
            title="Изменение типа отправки"
            centered
            open={isModalOpen}
            onClose={handleClose}
          >
            <Controller
              control={control}
              name="sentTo"
              rules={{
                required: 'Пожалуйста, выберите тип отправки'
              }}
              render={() => (
                <Select
                  label="Отправлять в"
                  options={sentToOptions}
                  selectedValue={sentToOptions.find(
                    (option) => option.value === selectedSentTo
                  )}
                  onChange={(value) =>
                    setSelectedSentTo(value.value as TSentTo)
                  }
                />
              )}
            />
            <div className="sent-to-switch__actions">
              <Button variant="outlined" type="button" onClick={handleClose}>
                Отмена
              </Button>
              <Button variant="filled-red" type="button" onClick={handleSubmit}>
                Изменить
              </Button>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

export default SentToSwitch;
